import {
  instance as axios,
  setJwtToken,
} from "../../config/axiosInstance";

/**
 * Send Interakt Event for a user
 * @param {Object} eventObj - event details
 * @returns {Promise<Object>} API response
 */
export const sendInteraktEventApi = async (eventObj) => {
  try {
    // Use analytics endpoint for now
    const { data } = await axios.post('/analytics/log-event', eventObj, setJwtToken());
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get common events used in segmentation
 * @returns {Promise<Array<string>>} Array of common event names
 */
export const getCommonEventsApi = async () => {
  try {
    const token = localStorage.getItem("vedic_token");
    if (!token) {
      console.error("No token found in localStorage");
      return [];
    }

    const { data } = await axios.get("/v1/segments/common-events", {
      ...setJwtToken(),
    });

    if (data && data.success && Array.isArray(data.data)) {
      return data.data;
    } else {
      console.error("Failed to fetch common events - invalid response:", data);
      return [];
    }
  } catch (error) {
    console.error("Error fetching common events:", error.response?.data || error.message);
    if (error.response?.status === 401 || error.response?.data?.code === 401) {
      console.error("Authentication failed - token may be invalid or expired");
    }
    return [];
  }
};

/**
 * Get common traits used in segmentation
 * @returns {Promise<Array<string>>} Array of common trait keys
 */
export const getCommonTraitsApi = async () => {
  try {
    const token = localStorage.getItem("vedic_token");
    if (!token) {
      console.error("No token found in localStorage");
      return [];
    }

    const { data } = await axios.get("/v1/segments/common-traits", {
      ...setJwtToken(),
    });

    if (data && data.success && Array.isArray(data.data)) {
      return data.data;
    } else {
      console.error("Failed to fetch common traits - invalid response:", data);
      return [];
    }
  } catch (error) {
    console.error("Error fetching common traits:", error.response?.data || error.message);
    if (error.response?.status === 401 || error.response?.data?.code === 401) {
      console.error("Authentication failed - token may be invalid or expired");
    }
    return [];
  }
};

/**
 * Get segment by ID
 * @param {string} id - Segment ID
 * @returns {Promise<Object>} Segment data
 */
export const getSegmentApi = async (id) => {
  try {
    const { data } = await axios.get(`/v1/segments/${id}`, setJwtToken());
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || "Failed to fetch segment");
    }
  } catch (error) {
    console.error("Error fetching segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Create new segment
 * @param {Object} segmentData - Segment data
 * @returns {Promise<Object>} Created segment
 */
export const createSegmentApi = async (segmentData) => {
  try {
    const { data } = await axios.post("/v1/segments", segmentData, setJwtToken());
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || "Failed to create segment");
    }
  } catch (error) {
    console.error("Error creating segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update segment
 * @param {string} id - Segment ID
 * @param {Object} segmentData - Updated segment data
 * @returns {Promise<Object>} Updated segment
 */
export const updateSegmentApi = async (id, segmentData) => {
  try {
    const { data } = await axios.put(`/v1/segments/${id}`, segmentData, setJwtToken());
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || "Failed to update segment");
    }
  } catch (error) {
    console.error("Error updating segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete segment
 * @param {string} id - Segment ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteSegmentApi = async (id) => {
  try {
    const { data } = await axios.delete(`/v1/segments/${id}`, setJwtToken());
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to delete segment");
    }
  } catch (error) {
    console.error("Error deleting segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Clone segment
 * @param {string} id - Segment ID to clone
 * @param {Object} options - Optional clone options (name, interakt_tag)
 * @returns {Promise<Object>} Cloned segment
 */
export const cloneSegmentApi = async (id, options = {}) => {
  try {
    const { data } = await axios.post(`/v1/segments/${id}/clone`, options, setJwtToken());
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || "Failed to clone segment");
    }
  } catch (error) {
    console.error("Error cloning segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Backfill segment (evaluate all users)
 * @param {string} id - Segment ID
 * @returns {Promise<Object>} Backfill result
 */
export const backfillSegmentApi = async (id) => {
  try {
    const { data } = await axios.post(`/v1/segments/${id}/backfill`, {}, setJwtToken());
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to backfill segment");
    }
  } catch (error) {
    console.error("Error backfilling segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get segment users
 * @param {string} id - Segment ID
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Object>} Users in segment
 */
export const getSegmentUsersApi = async (id, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/v1/segments/${id}/users${queryParams ? `?${queryParams}` : ''}`;
    const { data } = await axios.get(endpoint, setJwtToken());
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to fetch segment users");
    }
  } catch (error) {
    console.error("Error fetching segment users:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get segment logs
 * @param {string} id - Segment ID
 * @param {Object} params - Query parameters (page, limit)
 * @returns {Promise<Object>} Segment logs
 */
export const getSegmentLogsApi = async (id, params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/v1/segments/${id}/logs${queryParams ? `?${queryParams}` : ''}`;
    const { data } = await axios.get(endpoint, setJwtToken());
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to fetch segment logs");
    }
  } catch (error) {
    console.error("Error fetching segment logs:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Test segment evaluation for a user
 * @param {string} id - Segment ID
 * @param {string} userId - User ID to test
 * @returns {Promise<Object>} Test result
 */
export const testSegmentApi = async (id, userId) => {
  try {
    const { data } = await axios.post(`/v1/segments/${id}/test`, { user_id: userId }, setJwtToken());
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to test segment");
    }
  } catch (error) {
    console.error("Error testing segment:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get all segments with filters
 * @param {Object} params - Query parameters (page, limit, status, search)
 * @returns {Promise<Object>} Segments list with pagination
 */
export const getSegmentsListApi = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const endpoint = `/v1/segments${queryParams ? `?${queryParams}` : ''}`;
    const { data } = await axios.get(endpoint, {
      ...setJwtToken(),
    });
    if (data.success) {
      return data;
    } else {
      throw new Error(data.message || "Failed to fetch segments");
    }
  } catch (error) {
    console.error("Error fetching segments list:", error.response?.data || error.message);
    throw error;
  }
};

